import { useMutation, useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import { showErrorToast, toast } from '@/components/ui/use-toast';
import { authenticationSession } from '@/lib/authentication-session';
import { downloadFile } from '@/lib/utils';
import {
  assertNotNullOrUndefined,
  ListFlowsRequest,
  FlowVersionMetadata,
  PopulatedFlow,
  FlowVersion,
  FlowOperationType,
  isNil,
} from '@activepieces/shared';

import { flowsApi } from './flows-api';
import { flowsUtils } from './flows-utils';

export const flowsHooks = {
  /** Fetches a single flow, skipped while flowId is nil. Shares the ['flow', id]
   * cache entry with the builder page loader. */
  useFlow: (flowId: string | undefined | null) => {
    return useQuery<PopulatedFlow, Error>({
      queryKey: ['flow', flowId],
      queryFn: () => {
        assertNotNullOrUndefined(flowId, 'flowId');
        return flowsApi.get(flowId);
      },
      enabled: !isNil(flowId),
      retry: false,
      staleTime: 5 * 1000,
    });
  },
  useFlows: (request: Omit<ListFlowsRequest, 'projectId'>) => {
    return useQuery({
      queryKey: ['flows', authenticationSession.getProjectId()],
      queryFn: async () => {
        return await flowsApi.list({
          ...request,
          projectId: authenticationSession.getProjectId()!,
        });
      },
      staleTime: 5 * 1000,
    });
  },
  useExportFlows: () => {
    return useMutation({
      mutationFn: async (flows: PopulatedFlow[]) => {
        if (flows.length === 0) {
          return flows;
        }
        if (flows.length === 1) {
          await flowsUtils.downloadFlow(flows[0].id);
          return flows;
        }
        await downloadFile({
          obj: await flowsUtils.zipFlows(flows),
          fileName: 'flows',
          extension: 'zip',
        });
        return flows;
      },
      onSuccess: (res) => {
        if (res.length > 0) {
          toast({
            variant: 'success',
            title: t('Success'),
            description:
              res.length === 1
                ? t(`${res[0].version.displayName} has been exported.`)
                : t('Flows have been exported.'),
            duration: 3000,
          });
        }
      },
      onError: (error) => showErrorToast(error),
    });
  },

  useFetchFlowVersion: ({
    onSuccess,
  }: {
    onSuccess: (flowVersion: FlowVersion) => void;
  }) => {
    return useMutation<FlowVersion, Error, FlowVersionMetadata>({
      mutationFn: async (flowVersion) => {
        const result = await flowsApi.get(flowVersion.flowId, {
          versionId: flowVersion.id,
        });
        return result.version;
      },
      onSuccess,
      onError: (error) => {
        showErrorToast(error);
        console.error(error);
      },
    });
  },
  useOverWriteDraftWithVersion: ({
    onSuccess,
  }: {
    onSuccess: (flowVersion: PopulatedFlow) => void;
  }) => {
    return useMutation<PopulatedFlow, Error, FlowVersionMetadata>({
      mutationFn: async (flowVersion) => {
        const result = await flowsApi.update(flowVersion.flowId, {
          type: FlowOperationType.USE_AS_DRAFT,
          request: {
            versionId: flowVersion.id,
          },
        });
        return result;
      },
      onSuccess,
      onError: (error) => {
        showErrorToast(error);
        console.error(error);
      },
    });
  },
};
